import { Injectable } from '@angular/core';
import { Observable, concatMap, forkJoin, map } from 'rxjs';

import {
  CanadianWorkersCompensation,
  InsurancePolicy
} from '../classes/models';
import { ComplianceSetting } from '../classes/models/compliance-setting';
import { GenericObject } from '../interfaces/generic-object';
import { InsurancePolicyTypeAbbreviated } from '../interfaces/insurance-policy-type-abbreviation';
import { CompanyVendorService } from './company-vendor.service';
import { ComplianceService } from './compliance.service';
import { UtilityService } from './utility.service';

/**
 * VPDW-4205: "is this vendor's required insurance currently expired?" was answered in two
 * places that disagreed. `ComplianceInsuranceReviewComponent` recomputed it from the live
 * policies, while `ComplianceReviewComponent.askToApproveVendor()` never asked at all — it
 * trusted the Insurance `ViveProgress.status === 'Passed'`. That row is *deliberately* left
 * `Passed` when coverage lapses (VPDW-4150 records the lapse in
 * `subStatus = 'Coverage Issues'` instead), so the vendor-level approve prompt fired at the
 * same time the insurance step was reporting expired policies — two contradictory modals,
 * and a one-click path to approving a vendor with no coverage.
 *
 * This service is the one answer. It owns both the predicate and the data the predicate
 * needs, so it can be consulted from the review host before any step component exists —
 * `<router-outlet>` is gated behind `@if (CompanyVendor && …)`, and the insurance step never
 * mounts at all on the other four routes, so a gate that asked the child would have gone
 * quiet exactly where it matters most.
 *
 * Provided on `ComplianceReviewComponent` (not `providedIn: 'root'`) so it is scoped to one
 * review session, the same way `SharedDataService` and `ComplianceStepStatusService` are.
 */
@Injectable()
export class InsuranceExpirationService {
  constructor(
    private CompanyVendorService: CompanyVendorService,
    private ComplianceService: ComplianceService,
    private UtilityService: UtilityService
  ) {}

  // The policy types a ComplianceSetting can require, and the flag that says so.
  private readonly PolicyTypeToRequirementMap: {
    GL: 'generalLiability_isRequired';
    AUTO: 'autoLiability_isRequired';
    UMB: 'umbrellaLiability_isRequired';
    WC: 'workersCompensation_isRequired';
    PL: 'errorsAndOmissions_isRequired';
  } = {
    GL: 'generalLiability_isRequired',
    AUTO: 'autoLiability_isRequired',
    UMB: 'umbrellaLiability_isRequired',
    WC: 'workersCompensation_isRequired',
    PL: 'errorsAndOmissions_isRequired'
  };

  private readonly PolicyTypesThatCanBeRequired: InsurancePolicyTypeAbbreviated[] =
    ['GL', 'AUTO', 'UMB', 'WC', 'PL'];

  private ComplianceSetting: ComplianceSetting | null = null;
  private clientCountry: 'US' | 'CA' | null = null;
  private PolicyTypeToInsurancePolicyMap = new Map<
    InsurancePolicyTypeAbbreviated,
    InsurancePolicy
  >();
  private CanadianWorkersCompensations: CanadianWorkersCompensation[] = [];

  /**
   * Loads the ComplianceSetting requirement flags, the live policies per type, and — only
   * where the client and vendor actually share Ontario / Alberta — the Canadian Workers'
   * Comp records. The filters mirror `ComplianceInsuranceReviewComponent`'s own load
   * exactly; drift between the two queries would put the contradiction back.
   */
  public load(
    CompanyVendorId: number,
    CompanyId: number,
    VendorId: number
  ): Observable<void> {
    return forkJoin([
      this.CompanyVendorService.getCompanyVendorCount({
        CompanyId,
        VendorId,
        isArchived: true
      }),
      this.ComplianceService.getClientVendorSharedStates(CompanyId, VendorId)
    ]).pipe(
      concatMap(([countOfArchivedRelationships, ClientVendorSharedStates]) => {
        const ClientVendorSharedStatesSet = new Set(ClientVendorSharedStates);

        const CompanyVendorQuery: GenericObject = {
          attributes: ['id'],
          id: CompanyVendorId,
          Company: {
            attributes: ['id', 'country'],
            ComplianceSetting: {
              options: { required: false },
              CompanyComplianceTypeId: {
                $col: 'CompanyVendor.CompanyComplianceTypeId'
              },
              literals: {
                id: { $eq: 'selectComplianceSettingIdForVendorRiskLevel' }
              }
            }
          },
          InsuranceCertificate: {
            status: { $notIn: ['Archived', 'Replaced'] },
            InsurancePolicy: {
              options: { required: true }
            }
          }
        };

        if (countOfArchivedRelationships > 0) {
          Object.assign(CompanyVendorQuery, { isArchived: true });
        }

        if (ClientVendorSharedStatesSet.has('ON')) {
          CompanyVendorQuery['WSIB'] = {};
        }

        if (ClientVendorSharedStatesSet.has('AB')) {
          CompanyVendorQuery['WCB'] = {};
        }

        return this.CompanyVendorService.getCompanyVendor(
          this.UtilityService.stringifyObjectsInQueryObject(CompanyVendorQuery)
        );
      }),
      map((CompanyVendor): void => {
        const { Company, InsuranceCertificates, WSIB, WCB } =
          CompanyVendor || {};
        const { ComplianceSettings, country } = Company || {};

        this.clientCountry = country || null;

        // The risk-level literal resolves to exactly one setting; anything else means we
        // cannot say what this client requires, so the predicate stays silent.
        this.ComplianceSetting =
          Array.isArray(ComplianceSettings) && ComplianceSettings.length === 1
            ? ComplianceSettings[0]
            : null;

        this.PolicyTypeToInsurancePolicyMap.clear();

        if (Array.isArray(InsuranceCertificates)) {
          InsuranceCertificates.forEach(({ InsurancePolicies }) => {
            if (!Array.isArray(InsurancePolicies)) {
              return;
            }

            InsurancePolicies.forEach((InsurancePolicy) => {
              const { policyType } = InsurancePolicy;

              if (policyType) {
                this.PolicyTypeToInsurancePolicyMap.set(
                  policyType,
                  InsurancePolicy
                );
              }
            });
          });
        }

        this.CanadianWorkersCompensations = [];

        if (WCB) {
          this.CanadianWorkersCompensations.push(WCB);
        }

        if (WSIB) {
          this.CanadianWorkersCompensations.push(WSIB);
        }
      })
    );
  }

  /**
   * True when a policy type this client requires has lapsed. Field-level compliance
   * findings can still read PASSED / NOT REQUIRED after a policy expires (CBRI-2136), and
   * the Insurance progress card can still read `Passed` (VPDW-4150), so anything gating on
   * "insurance is fine" has to ask the policy dates rather than either of those.
   *
   * Deliberately *not* gated on the Insurance `subStatus = 'Coverage Issues'` stamp:
   * VPDW-4059's sweep applies it without checking for an active unexpired replacement
   * policy, so it would refuse approval for vendors whose replacement COI is already on
   * file.
   */
  public hasExpiredRequiredInsurance(): boolean {
    const ComplianceSetting = this.ComplianceSetting;

    if (!ComplianceSetting) {
      return false;
    }

    if (this.clientCountry === 'CA' && this.hasExpiredCanadianWorkersComp()) {
      return true;
    }

    const nowMs = Date.now();

    return this.PolicyTypesThatCanBeRequired.some((policyType) => {
      const requirementKey = this.PolicyTypeToRequirementMap[policyType];

      if (!ComplianceSetting[requirementKey]) {
        return false;
      }

      const policy = this.PolicyTypeToInsurancePolicyMap.get(policyType);

      if (!policy) {
        return false;
      }

      if (policy.status === 'Expired') {
        return true;
      }

      if (policy.expirationDate) {
        const expirationDate =
          policy.expirationDate instanceof Date
            ? policy.expirationDate
            : new Date(policy.expirationDate);

        if (expirationDate.getTime() <= nowMs) {
          return true;
        }
      }

      return false;
    });
  }

  // EXPIRED takes precedence over every other Canadian Workers' Comp result in the
  // insurance step's own evaluation, and it is the only one this predicate cares about.
  private hasExpiredCanadianWorkersComp(): boolean {
    const WorkersCompRequired =
      !!this.ComplianceSetting?.workersCompensation_isRequired;

    return this.CanadianWorkersCompensations.some((CWC) => {
      const { result } =
        this.ComplianceService.calculateCanadianCompensationFindingAndResult(
          CWC,
          WorkersCompRequired
        );

      return result === 'EXPIRED';
    });
  }
}
